import {
    BLOCK_SIZE,
    BLOCK_SHAPES,
    BUILDER_BUDGET,
    DEFAULT_BLOCK_SHAPE,
    DEFAULT_BOMBS,
    DEMO_BUILDS,
    MATERIALS,
    WORLD
} from './constants.js'
import { buildPhysicsGraph, computeStability } from './physics-analysis.js'
import { getBlockMaxHealth } from './physics-damage.js'

const MAP_VERSION = 1
const HALF_COST_GLASS_SHAPES = new Set(['chip', 'sliver'])
const MAX_TOTAL_AMMO = 6
const BASELINE_BASIC_AMMO = 1
const MAX_BASIC_AMMO = 4
const AMMO_THREAT = {
    basic: 1,
    fire: 1.4,
    iron: 1.3,
    explosive: 3.6
}

export function createId() {
    if (crypto.randomUUID) {
        return crypto.randomUUID()
    }

    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (char) => (
        Number(char) ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(char) / 4)))
    ).toString(16))
}

export function cloneBlocks(blocks) {
    return blocks.map((block) => {
        const shapeKey = block.shape && BLOCK_SHAPES[block.shape]
            ? block.shape
            : inferShapeKeyFromDimensions(block)
        const shape = BLOCK_SHAPES[shapeKey]

        return {
            id: block.id || createId('block'),
            material: block.material,
            shape: shapeKey,
            x: Number(block.x),
            y: Number(block.y),
            width: Number(block.width ?? shape?.width ?? BLOCK_SIZE.width),
            height: Number(block.height ?? shape?.height ?? BLOCK_SIZE.height),
            rotation: Number(block.rotation || 0),
            health: block.health || MATERIALS[block.material]?.health || 80
        }
    })
}

export function calculateBudgetUsed(blocks) {
    return blocks.reduce((total, block) => total + getBlockCost(block), 0)
}

export function getBlockCost(block) {
    const materialKey = MATERIALS[block.material] ? block.material : 'wood'
    const shapeKey = block.shape && BLOCK_SHAPES[block.shape]
        ? block.shape
        : inferShapeKeyFromDimensions(block)
    const material = MATERIALS[materialKey]
    const shape = BLOCK_SHAPES[shapeKey]
    const multiplier = shape?.costMultiplier ?? 1

    if (materialKey === 'glass' && HALF_COST_GLASS_SHAPES.has(shapeKey)) {
        return 0.5
    }

    return Math.max(1, Math.round(material.cost * multiplier))
}

export function calculateMaterialCounts(blocks) {
    return blocks.reduce(
        (counts, block) => {
            counts[block.material] += 1
            return counts
        },
        { stone: 0, wood: 0, glass: 0 }
    )
}

export function calculateBombsGranted(blocks) {
    const stats = calculateFortificationStats(blocks)
    const stone = stats.materials.stone
    const wood = stats.materials.wood
    const glass = stats.materials.glass
    const woodUnlocked = wood.count >= 3 || wood.area >= 8
    const glassUnlocked = glass.count >= 2 || glass.area >= 4
    const glassBombUnlocked = glass.count > 7
    const blastUnlocked = stone.count >= 5 && stone.area >= 12 && stone.fp >= 420
    const blastAmmo = blastUnlocked
        ? 1 + Math.floor((stone.fp - 420) / 520)
        : 0
    const ammo = {
        basic: clampInteger(DEFAULT_BOMBS.basic + Math.floor(stats.totalFP / 450), BASELINE_BASIC_AMMO, MAX_BASIC_AMMO),
        explosive: clampInteger(Math.max(blastAmmo, glassBombUnlocked ? 1 : 0), 0, 3),
        fire: woodUnlocked ? clampInteger(Math.floor(wood.fp / 240), 0, 5) : 0,
        iron: glassUnlocked ? clampInteger(Math.max(1, Math.floor(glass.fp / 110)), 0, 4) : 0
    }

    return capTotalAmmo(ammo, stats)
}

export function countBombs(bombs) {
    return Object.values(bombs).reduce((total, amount) => total + amount, 0)
}

export function calculateComplexity(blocks) {
    const stats = calculateFortificationStats(blocks)
    const bombs = calculateBombsGranted(blocks)
    const structureScore = Math.round(stats.totalFP / 80)
    const ammoThreat = Object.entries(bombs).reduce(
        (total, [type, amount]) => total + amount * (AMMO_THREAT[type] || 1),
        0
    )

    return Math.round(blocks.length + structureScore + ammoThreat)
}

export function buildMapData({ title, builderName, xHandle, builderId, blocks, flag, previewImageUrl = '' }) {
    const normalizedBlocks = cloneBlocks(blocks)
    const bombsGranted = calculateBombsGranted(normalizedBlocks)
    const normalizedFlag = normalizeFlag(flag)

    return {
        version: MAP_VERSION,
        title: normalizeText(title, 'Untitled Fortress', 64),
        builderId,
        builderName: normalizeText(builderName, 'Builder', 40),
        xHandle: normalizeHandle(xHandle),
        blocks: normalizedBlocks,
        flag: normalizedFlag,
        blockCount: normalizedBlocks.length,
        budgetUsed: calculateBudgetUsed(normalizedBlocks),
        complexity: calculateComplexity(normalizedBlocks),
        bombsGranted,
        previewImageUrl: normalizeImageUrl(previewImageUrl),
        createdAt: new Date().toISOString()
    }
}

export function normalizeBuildRow(row) {
    const map = row.map_data || row.mapData || row
    const blocks = cloneBlocks(map.blocks || [])
    const bombsGranted = normalizeBombsGranted(row.bombs_granted || map.bombsGranted, blocks)

    return {
        id: row.id || map.id || createId('local-build'),
        title: row.title || map.title || 'Untitled Fortress',
        builderId: row.builder_id || map.builderId,
        builderName: map.builderName || row.builder_name || 'Builder',
        xHandle: map.xHandle || '',
        blocks,
        flag: normalizeFlag(map.flag),
        blockCount: row.block_count ?? map.blockCount ?? blocks.length,
        budgetUsed: row.budget_used ?? map.budgetUsed ?? calculateBudgetUsed(blocks),
        complexity: calculateComplexity(blocks),
        bombsGranted,
        ogImageUrl: row.og_image_url || map.ogImageUrl || '',
        previewImageUrl: row.preview_image_url || row.previewImageUrl || map.previewImageUrl || '',
        playCount: row.play_count ?? map.playCount ?? 0,
        winCount: row.win_count ?? map.winCount ?? 0,
        createdAt: row.created_at || map.createdAt || new Date().toISOString(),
        source: row.source || 'neon'
    }
}

export function createDemoBuilds() {
    return DEMO_BUILDS.map((demo, index) => {
        const map = buildMapData({
            title: demo.title,
            builderName: demo.builderName,
            xHandle: demo.xHandle,
            builderId: `demo-${index + 1}`,
            blocks: demo.blocks,
            flag: demo.flag
        })

        return normalizeBuildRow({
            id: `demo-${index + 1}`,
            title: map.title,
            map_data: map,
            block_count: map.blockCount,
            budget_used: map.budgetUsed,
            complexity: map.complexity,
            bombs_granted: map.bombsGranted,
            play_count: 0,
            win_count: 0,
            created_at: map.createdAt,
            source: 'demo'
        })
    })
}

export function canPublishBuild(blocks) {
    const budgetUsed = calculateBudgetUsed(blocks)

    if (blocks.length < 5) {
        return { ok: false, reason: 'Place at least 5 blocks before publishing.' }
    }

    if (budgetUsed > BUILDER_BUDGET) {
        return { ok: false, reason: 'The build is over budget.' }
    }

    return { ok: true, reason: 'Ready to publish.' }
}

export function encodeMap(mapData) {
    const json = JSON.stringify(sanitizeMapForUrl(mapData))
    return btoa(encodeURIComponent(json))
        .replaceAll('+', '-')
        .replaceAll('/', '_')
        .replaceAll('=', '')
}

export function decodeMap(encoded) {
    try {
        const padded = encoded.padEnd(encoded.length + ((4 - (encoded.length % 4)) % 4), '=')
        const json = decodeURIComponent(atob(padded.replaceAll('-', '+').replaceAll('_', '/')))
        return normalizeBuildRow({ ...JSON.parse(json), source: 'url' })
    } catch (error) {
        console.warn('Unable to decode shared map', error)
        return null
    }
}

export function createShareUrl(build, baseUrl = window.location.href) {
    const url = new URL(baseUrl)
    url.search = ''

    if ((build.source === 'neon' || build.source === 'remote') && build.id) {
        url.searchParams.set('build', build.id)
    } else {
        url.searchParams.set('map', encodeMap(build))
    }

    return url.toString()
}

export function normalizeText(value, fallback, maxLength) {
    const normalized = String(value || '').trim().slice(0, maxLength)
    return normalized || fallback
}

export function normalizeHandle(value) {
    const handle = String(value || '').trim().replace(/^@/, '').slice(0, 30)
    return handle || null
}

function normalizeFlag(flag) {
    return {
        x: Number(flag?.x ?? 10.5),
        y: Number(flag?.y ?? -5.0)
    }
}

function inferShapeFromDimensions(block) {
    return BLOCK_SHAPES[inferShapeKeyFromDimensions(block)]
}

function inferShapeKeyFromDimensions(block) {
    const width = Number(block?.width || BLOCK_SIZE.width)
    const height = Number(block?.height || BLOCK_SIZE.height)

    const match = Object.entries(BLOCK_SHAPES).find(([, shape]) => (
        Math.abs(shape.width - width) < 0.001
        && Math.abs(shape.height - height) < 0.001
    ))

    return match?.[0] || DEFAULT_BLOCK_SHAPE
}

function normalizeBombsGranted(_savedBombs, blocks) {
    return calculateBombsGranted(blocks)
}

function calculateFortificationStats(blocks) {
    const stats = {
        totalFP: 0,
        materials: {
            stone: createMaterialFortificationStats(),
            wood: createMaterialFortificationStats(),
            glass: createMaterialFortificationStats()
        }
    }

    if (!blocks?.length) {
        return stats
    }

    const graph = buildPhysicsGraph(blocks, { floorY: WORLD.buildMinY })

    for (const block of blocks) {
        const materialStats = stats.materials[block.material]

        if (!materialStats) {
            continue
        }

        const stabilityScore = computeStability(block.id, graph)
        const stability = Number.isFinite(stabilityScore) ? stabilityScore : 0.5
        const supportScale = clamp(0.7 + stability, 0.7, 1.7)
        const fortificationPoints = getBlockMaxHealth(block) * supportScale

        materialStats.count += 1
        materialStats.area += getBlockGridArea(block)
        materialStats.fp += fortificationPoints
        stats.totalFP += fortificationPoints
    }

    return stats
}

function createMaterialFortificationStats() {
    return { count: 0, area: 0, fp: 0 }
}

function getBlockGridArea(block) {
    const shape = block?.shape && BLOCK_SHAPES[block.shape]
        ? BLOCK_SHAPES[block.shape]
        : inferShapeFromDimensions(block)

    if (Number.isFinite(shape?.gridWidth) && Number.isFinite(shape?.gridHeight)) {
        return Math.max(0.2, shape.gridWidth * shape.gridHeight)
    }

    const width = Number(block?.width || shape?.width || BLOCK_SIZE.width)
    const height = Number(block?.height || shape?.height || BLOCK_SIZE.height)
    return Math.max(0.2, (width * height) / (WORLD.grid * WORLD.grid))
}

function capTotalAmmo(ammo, stats) {
    const capped = { ...ammo }
    const majorMaterialMin = (material) => (
        stats.totalFP > 0 && stats.materials[material].fp >= stats.totalFP * 0.25 ? 1 : 0
    )
    const reductions = [
        { type: 'basic', min: BASELINE_BASIC_AMMO },
        { type: 'fire', min: majorMaterialMin('wood') },
        { type: 'iron', min: majorMaterialMin('glass') },
        { type: 'explosive', min: majorMaterialMin('stone') }
    ]

    for (const reduction of reductions) {
        while (countBombs(capped) > MAX_TOTAL_AMMO && capped[reduction.type] > reduction.min) {
            capped[reduction.type] -= 1
        }
    }

    return capped
}

function clampInteger(value, min, max) {
    return Math.round(clamp(value, min, max))
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value))
}

function sanitizeMapForUrl(mapData) {
    if (!isDataImageUrl(mapData?.previewImageUrl)) {
        return mapData
    }

    return {
        ...mapData,
        previewImageUrl: ''
    }
}

function normalizeImageUrl(value) {
    const imageUrl = String(value || '').trim()

    if (isDataImageUrl(imageUrl)) {
        return imageUrl
    }

    try {
        const url = new URL(imageUrl)
        return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : ''
    } catch {
        return ''
    }
}

function isDataImageUrl(value) {
    return /^data:image\/(?:png|jpeg|jpg|webp);base64,/i.test(String(value || '').trim())
}
